import "./ClientEditScreen.scss";
import ClientBasicDetailsFormComponent from "../client-basic-details-form/ClientBasicDetailsFormComponent";
import {useCallback, useEffect, useState} from "react";
import {setCurrentNavParams} from "../../../../store/actions/navigation.action";
import {useDispatch, useSelector} from "react-redux";
import ClientPersonalHabitsFormComponent from "../client-personal-habits-form/ClientPersonalHabitsFormComponent";
import {IClientFormSteps} from "../../../../shared/models/client.model";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {CommonService} from "../../../../shared/services";
import ClientMedicalHistoryFormComponent from "../client-medical-history-form/ClientMedicalHistoryFormComponent";
import ClientMedicalSupplementsFormComponent
    from "../client-medical-supplements-form/ClientMedicalSupplementsFormComponent";
import ClientSurgicalHistoryFormComponent from "../client-surgical-history-form/ClientSurgicalHistoryFormComponent";
import ClientMedicalFemaleOnlyFormComponent
    from "../client-medical-female-only-form/ClientMedicalFemaleOnlyFormComponent";
import ClientMedicalProviderInformationFormComponent
    from "../client-medical-provider-information-form/ClientMedicalProviderInformationFormComponent";
import ClientMusculoskeletalHistoryFormComponent
    from "../client-musculoskeletal-history-form/ClientMusculoskeletalHistoryFormComponent";
import ClientAccountDetailsFormComponent from "../client-account-details-form/ClientAccountDetailsFormComponent";
import {IRootReducerState} from "../../../../store/reducers";
import ClientAllergiesFormComponent from "../client-allergies-form/ClientAllergiesFormComponent";
import {
    getClientMedicalHistoryDetails
} from "../../../../store/actions/client.action";

interface ClientEditScreenProps {

}

const ClientAddSteps: IClientFormSteps[] = ["basicDetails", "personalHabits", "allergies", "medicalSupplements", "surgicalHistory", "medicalFemaleOnly", "medicalProvider", "musculoskeletalHistory", "medicalHistory", "accountDetails"];

const ClientEditScreen = (props: ClientEditScreenProps) => {

    const navigate = useNavigate();
    const {clientId}:any = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentStep, setCurrentStep] = useState<IClientFormSteps>(ClientAddSteps[0]);
    const dispatch = useDispatch();
    const {
        clientBasicDetails,
        isClientBasicDetailsLoading,
        isClientBasicDetailsLoadingFailed
    } = useSelector((state: IRootReducerState) => state.client);

    const {
        clientMedicalHistoryDetails,
        isClientMedicalHistoryDetailsLoaded,
        isClientMedicalHistoryDetailsLoading,
        isClientMedicalHistoryDetailsLoadingFailed
    } = useSelector((state: IRootReducerState) => state.client);

    const {
        clientCommunicationReferralDetails,
        isClientCommunicationReferralDetailsLoaded,
        isClientCommunicationReferralDetailsLoading,
        isClientCommunicationReferralDetailsLoadingFailed,
    } = useSelector((state: IRootReducerState) => state.client);

    useEffect(() => {
        dispatch(setCurrentNavParams('Edit Client', null, () => {
            if (clientId) {
                if (currentStep === "basicDetails") {
                    navigate(CommonService._client.NavigateToClientDetails("basicDetails"));
                } else if (currentStep === "accountDetails") {
                    navigate(CommonService._client.NavigateToClientDetails("accountDetails"));
                } else {
                    navigate(CommonService._client.NavigateToClientDetails("medicalHistoryQuestionnaire"));
                }
            }
        }));
    }, [dispatch, currentStep, clientId, navigate]);

    const handleClientDetailsSave = useCallback((data: any) => {
        switch (currentStep) {
            case "basicDetails": {
                navigate(CommonService._client.NavigateToClientDetails("basicDetails"));
                break;
            }
            case "accountDetails": {
                navigate(CommonService._routeConfig.ProfilePreferencesDetails());
                break;
            }
            case "personalHabits":
            case "allergies":
            case "medicalHistory":
            case "medicalSupplements":
            case "medicalFemaleOnly":
            case "surgicalHistory":
            case "musculoskeletalHistory":
            case "medicalProvider": {
                dispatch(getClientMedicalHistoryDetails());
                navigate(CommonService._routeConfig.ProfileMedicalHistoryDetails());
                break;
            }
            default: {
                // navigate(CommonService._routeConfig.ClientList());
            }
        }
    }, [currentStep, navigate, dispatch]);

    const handleClientDetailsNext = useCallback(() => {
        let nextStep = currentStep;
        switch (currentStep) {
            case "personalHabits": {
                nextStep = 'allergies';
                break;
            }
            case "allergies": {
                nextStep = 'medicalSupplements';
                break;
            }
            case "medicalSupplements": {
                nextStep = 'medicalHistory';
                break;
            }
            case "medicalHistory": {
                if (clientBasicDetails?.gender === "female") {
                    nextStep = 'medicalFemaleOnly';
                } else {
                    nextStep = 'surgicalHistory';
                }
                break;
            }
            case "medicalFemaleOnly": {
                nextStep = 'surgicalHistory';
                break;
            }
            case "surgicalHistory": {
                nextStep = 'musculoskeletalHistory';
                break;
            }
            case "musculoskeletalHistory": {
                nextStep = 'medicalProvider';
                break;
            }
            case "medicalProvider": {
                nextStep = 'accountDetails';
                break;
            }
            case "accountDetails": {
                // navigate(CommonService._routeConfig.ClientList());
                return;
            }
            default: {
                navigate(CommonService._client.NavigateToClientDetails("basicDetails"));
                return;
            }
        }
        setCurrentStep(nextStep);
        searchParams.set("currentStep", nextStep);
        setSearchParams(searchParams);
    }, [currentStep, searchParams, setSearchParams, navigate, clientBasicDetails]);

    const handleClientDetailsCancel = useCallback(() => {
        switch (currentStep) {
            case "accountDetails": {
                navigate(CommonService._routeConfig.ProfilePreferencesDetails());
                break
            }
            case "basicDetails": {
                navigate(CommonService._client.NavigateToClientDetails(currentStep));
                break;
            }
            case "personalHabits":
            case "allergies":
            case "medicalHistory":
            case 'medicalSupplements':
            case "medicalFemaleOnly":
            case "surgicalHistory":
            case "musculoskeletalHistory":
            case "medicalProvider": {
                navigate(CommonService._routeConfig.ProfileMedicalHistoryDetails());
                break;
            }
            default: {
                // navigate(CommonService._routeConfig.ClientList());
            }
        }
    }, [navigate, currentStep]);

    useEffect(() => {
        let currentStep: any = searchParams.get("currentStep");
        if (currentStep) {
            if (!ClientAddSteps.includes(currentStep)) {
                currentStep = "basicDetails";
            }
        } else {
            currentStep = "basicDetails";
        }
        setCurrentStep(currentStep);
    }, [searchParams]);


    return (
        <div className={'client-edit-screen'}>
            {
                currentStep === "basicDetails" &&
                <ClientBasicDetailsFormComponent
                    isClientBasicDetailsLoading={isClientBasicDetailsLoading}
                    isClientBasicDetailsLoadingFailed={isClientBasicDetailsLoadingFailed}
                    clientBasicDetails={clientBasicDetails}
                    onSave={handleClientDetailsSave}
                    onCancel={handleClientDetailsCancel}
                />
            }
            <>

                {
                    currentStep === "personalHabits" && <ClientPersonalHabitsFormComponent
                        clientMedicalHistoryDetails={clientMedicalHistoryDetails}
                        isClientMedicalHistoryDetailsLoaded={isClientMedicalHistoryDetailsLoaded}
                        isClientMedicalHistoryDetailsLoading={isClientMedicalHistoryDetailsLoading}
                        isClientMedicalHistoryDetailsLoadingFailed={isClientMedicalHistoryDetailsLoadingFailed}
                        onSave={handleClientDetailsSave}
                        onCancel={handleClientDetailsCancel}
                        clientId={clientBasicDetails?._id}
                        onNext={handleClientDetailsNext}
                    />
                }
                {
                    currentStep === "medicalSupplements" && <ClientMedicalSupplementsFormComponent
                        clientMedicalHistoryDetails={clientMedicalHistoryDetails}
                        isClientMedicalHistoryDetailsLoaded={isClientMedicalHistoryDetailsLoaded}
                        isClientMedicalHistoryDetailsLoading={isClientMedicalHistoryDetailsLoading}
                        isClientMedicalHistoryDetailsLoadingFailed={isClientMedicalHistoryDetailsLoadingFailed}
                        onSave={handleClientDetailsSave}
                        clientId={clientBasicDetails?._id}
                        onCancel={handleClientDetailsCancel}
                        onNext={handleClientDetailsNext}
                    />
                }
                {
                    currentStep === "medicalHistory" && <ClientMedicalHistoryFormComponent
                        clientMedicalHistoryDetails={clientMedicalHistoryDetails}
                        isClientMedicalHistoryDetailsLoaded={isClientMedicalHistoryDetailsLoaded}
                        isClientMedicalHistoryDetailsLoading={isClientMedicalHistoryDetailsLoading}
                        isClientMedicalHistoryDetailsLoadingFailed={isClientMedicalHistoryDetailsLoadingFailed}
                        onSave={handleClientDetailsSave}
                        clientId={clientBasicDetails?._id}
                        onCancel={handleClientDetailsCancel}
                        onNext={handleClientDetailsNext}
                    />
                }
                {
                    currentStep === "surgicalHistory" && <ClientSurgicalHistoryFormComponent
                        clientMedicalHistoryDetails={clientMedicalHistoryDetails}
                        isClientMedicalHistoryDetailsLoaded={isClientMedicalHistoryDetailsLoaded}
                        isClientMedicalHistoryDetailsLoading={isClientMedicalHistoryDetailsLoading}
                        isClientMedicalHistoryDetailsLoadingFailed={isClientMedicalHistoryDetailsLoadingFailed}
                        onSave={handleClientDetailsSave}
                        clientId={clientBasicDetails?._id}
                        onCancel={handleClientDetailsCancel}
                        onNext={handleClientDetailsNext}
                    />
                }
                {
                    currentStep === "medicalFemaleOnly" && <ClientMedicalFemaleOnlyFormComponent
                        clientMedicalHistoryDetails={clientMedicalHistoryDetails}
                        isClientMedicalHistoryDetailsLoaded={isClientMedicalHistoryDetailsLoaded}
                        isClientMedicalHistoryDetailsLoading={isClientMedicalHistoryDetailsLoading}
                        isClientMedicalHistoryDetailsLoadingFailed={isClientMedicalHistoryDetailsLoadingFailed}
                        onSave={handleClientDetailsSave}
                        clientId={clientBasicDetails?._id}
                        onCancel={handleClientDetailsCancel}
                        onNext={handleClientDetailsNext}
                    />
                }
                {
                    currentStep === "medicalProvider" && <ClientMedicalProviderInformationFormComponent
                        clientMedicalHistoryDetails={clientMedicalHistoryDetails}
                        isClientMedicalHistoryDetailsLoaded={isClientMedicalHistoryDetailsLoaded}
                        isClientMedicalHistoryDetailsLoading={isClientMedicalHistoryDetailsLoading}
                        isClientMedicalHistoryDetailsLoadingFailed={isClientMedicalHistoryDetailsLoadingFailed}
                        onSave={handleClientDetailsSave}
                        clientId={clientBasicDetails?._id}
                        onCancel={handleClientDetailsCancel}
                        onNext={handleClientDetailsNext}
                    />
                }
                {
                    currentStep === "musculoskeletalHistory" && <ClientMusculoskeletalHistoryFormComponent
                        clientMedicalHistoryDetails={clientMedicalHistoryDetails}
                        isClientMedicalHistoryDetailsLoaded={isClientMedicalHistoryDetailsLoaded}
                        isClientMedicalHistoryDetailsLoading={isClientMedicalHistoryDetailsLoading}
                        isClientMedicalHistoryDetailsLoadingFailed={isClientMedicalHistoryDetailsLoadingFailed}
                        onSave={handleClientDetailsSave}
                        clientId={clientBasicDetails?._id}
                        onCancel={handleClientDetailsCancel}
                        onNext={handleClientDetailsNext}
                    />
                }
                {
                    currentStep === 'allergies' && <ClientAllergiesFormComponent
                        onSave={handleClientDetailsSave}
                        onCancel={handleClientDetailsCancel}
                        onNext={handleClientDetailsNext}
                        clientId={clientBasicDetails?._id}
                        clientMedicalHistoryDetails={clientMedicalHistoryDetails}
                        isClientMedicalHistoryDetailsLoaded={isClientMedicalHistoryDetailsLoaded}
                        isClientMedicalHistoryDetailsLoading={isClientMedicalHistoryDetailsLoading}
                        isClientMedicalHistoryDetailsLoadingFailed={isClientMedicalHistoryDetailsLoadingFailed}
                    />
                }
                {
                    currentStep === "accountDetails" && <ClientAccountDetailsFormComponent
                        onSave={handleClientDetailsSave}
                        onCancel={handleClientDetailsCancel}
                        clientId={clientBasicDetails?._id}
                        clientCommunicationReferralDetails={clientCommunicationReferralDetails}
                        isClientCommunicationReferralDetailsLoaded={isClientCommunicationReferralDetailsLoaded}
                        isClientCommunicationReferralDetailsLoading={isClientCommunicationReferralDetailsLoading}
                        isClientCommunicationReferralDetailsLoadingFailed={isClientCommunicationReferralDetailsLoadingFailed}
                    />
                }
            </>
        </div>
    )
};

export default ClientEditScreen;
